import React from "react";
import {ActionType, BaseComponent,} from "@reapptor-apps/reapptor-react-common";
import {ButtonType, CellAction, CellModel, ColumnActionDefinition, ColumnDefinition, ColumnType, Grid, GridHoveringType, GridModel, GridOddType, IconSize, Inline, JustifyContent, ToolbarButton} from "@reapptor-apps/reapptor-react-components";
import CruiseAreaPrice from "@/models/server/CruiseAreaPrice";
import CruiseArea from "@/models/server/CruiseArea";
import SaveCruiseAreaPricesRequest from "@/models/server/requests/SaveCruiseAreaPricesRequest";
import {CruiseDuration, CruiseGroupSize} from "@/models/Enums";
import {Utility} from "@reapptor-apps/reapptor-toolkit";
import EnumProvider from "@/providers/EnumProvider";
import Localizer from "../../../localization/Localizer";

import styles from "./AreaPricesPanel.module.scss";

interface IAreaPricesPanelProps {
    area: CruiseArea;
    groupSize: CruiseGroupSize | null;
}

interface IAreaPricesPanelState {
}

class PriceRow {
    public id: string = Utility.getComponentId();
    
    public isNew: boolean = false;
    
    public min: number = 0;
    
    public max: number = 0;
    
    public groupSize: CruiseGroupSize | null = null;
    
    public justRide: number = 0;
    
    public _1: number = 0;
    
    public _2: number = 0;
    
    public _3: number = 0;
    
    public _4: number = 0;
    
    public _5: number = 0;
    
    public setPrice(price: CruiseAreaPrice): void {
        this.min = price.min;
        this.max = price.max;
        switch (price.duration) {
            case CruiseDuration.JustRide: this.justRide = price.price; break;
            case CruiseDuration.H1: this._1 = price.price; break;
            case CruiseDuration.H2: this._2 = price.price; break;
            case CruiseDuration.H3: this._3 = price.price; break;
            case CruiseDuration.H4: this._4 = price.price; break;
            case CruiseDuration.H5: this._5 = price.price; break;
        }
    }
    
    public toPrices(): CruiseAreaPrice[] {
        return [
            PriceRow.createPrice(this.min, this.max, CruiseDuration.JustRide, this.justRide, this.groupSize),
            PriceRow.createPrice(this.min, this.max, CruiseDuration.H1, this._1, this.groupSize),
            PriceRow.createPrice(this.min, this.max, CruiseDuration.H2, this._2, this.groupSize),
            PriceRow.createPrice(this.min, this.max, CruiseDuration.H3, this._3, this.groupSize),
            PriceRow.createPrice(this.min, this.max, CruiseDuration.H4, this._4, this.groupSize),
            PriceRow.createPrice(this.min, this.max, CruiseDuration.H5, this._5, this.groupSize),
        ];
    }
    
    public static createPrice(min: number, max: number, duration: CruiseDuration, price: number, groupSize: CruiseGroupSize | null): CruiseAreaPrice {
        const item = new CruiseAreaPrice();
        item.min = min;
        item.max = max;
        item.duration = duration;
        item.price = price;
        item.groupSize = groupSize;
        return item;
    }
}

export default class AreaPricesPanel extends BaseComponent<IAreaPricesPanelProps, IAreaPricesPanelState> {

    state: IAreaPricesPanelState = {
    };
    
    private _rows: PriceRow[] | null = null;

    private readonly _areasGridRef: React.RefObject<Grid<PriceRow>> = React.createRef();

    private readonly _areasColumns: ColumnDefinition[] = [
        {
            header: "#",
            minWidth: 40,
            noWrap: true,
            className: "grey",
            accessor: "#"
        } as ColumnDefinition,
        {
            group: Localizer.areaPricesPanelAmountLanguageItemName,
            header: Localizer.areaPricesPanelMinLanguageItemName,
            accessor: nameof<PriceRow>(o => o.min),
            minWidth: 60,
            maxWidth: 60,
            type: ColumnType.Number,
            reRenderRow: true,
            settings: {
                min: 0,
                max: 999
            },
            init: (cell) => this.initAmount(cell)
        } as ColumnDefinition,
        {
            group: Localizer.areaPricesPanelAmountLanguageItemName,
            header: Localizer.areaPricesPanelMaxLanguageItemName,
            accessor: nameof<PriceRow>(o => o.max),
            minWidth: 60,
            maxWidth: 60,
            type: ColumnType.Number,
            reRenderRow: true,
            settings: {
                min: 0,
                max: 999
            },
            init: (cell) => this.initAmount(cell)
        } as ColumnDefinition,
        {
            group: Localizer.areaPricesPanelPriceLanguageItemName,
            header: EnumProvider.getCruiseDurationName(CruiseDuration.JustRide),
            accessor: nameof<PriceRow>(o => o.justRide),
            minWidth: 75,
            maxWidth: 75,
            noWrap: true,
            format: "C",
            type: ColumnType.Number,
            settings: {
                min: 0,
                max: 9999.99
            },
            init: (cell) => this.initPrice(cell)
        } as ColumnDefinition,
        {
            group: Localizer.areaPricesPanelPriceLanguageItemName,
            header: EnumProvider.getCruiseDurationName(CruiseDuration.H1),
            accessor: nameof<PriceRow>(o => o._1),
            minWidth: 75,
            maxWidth: 75,
            noWrap: true,
            format: "C",
            type: ColumnType.Number,
            settings: {
                min: 0,
                max: 9999.99
            },
            init: (cell) => this.initPrice(cell)
        } as ColumnDefinition,
        {
            group: Localizer.areaPricesPanelPriceLanguageItemName,
            header: EnumProvider.getCruiseDurationName(CruiseDuration.H2),
            accessor: nameof<PriceRow>(o => o._2),
            minWidth: 75,
            maxWidth: 75,
            noWrap: true,
            format: "C",
            type: ColumnType.Number,
            settings: {
                min: 0,
                max: 9999.99
            },
            init: (cell) => this.initPrice(cell)
        } as ColumnDefinition,
        {
            group: Localizer.areaPricesPanelPriceLanguageItemName,
            header: EnumProvider.getCruiseDurationName(CruiseDuration.H3),
            accessor: nameof<PriceRow>(o => o._3),
            minWidth: 75,
            maxWidth: 75,
            noWrap: true,
            format: "C",
            type: ColumnType.Number,
            settings: {
                min: 0,
                max: 9999.99
            },
            init: (cell) => this.initPrice(cell)
        } as ColumnDefinition,
        {
            group: Localizer.areaPricesPanelPriceLanguageItemName,
            header: EnumProvider.getCruiseDurationName(CruiseDuration.H4),
            accessor: nameof<PriceRow>(o => o._4),
            minWidth: 75,
            maxWidth: 75,
            noWrap: true,
            format: "C",
            type: ColumnType.Number,
            settings: {
                min: 0,
                max: 9999.99
            },
            init: (cell) => this.initPrice(cell)
        } as ColumnDefinition,
        {
            group: Localizer.areaPricesPanelPriceLanguageItemName,
            header: EnumProvider.getCruiseDurationName(CruiseDuration.H5),
            accessor: nameof<PriceRow>(o => o._5),
            minWidth: 75,
            maxWidth: 75,
            noWrap: true,
            format: "C",
            type: ColumnType.Number,
            settings: {
                min: 0,
                max: 9999.99
            },
            init: (cell) => this.initPrice(cell)
        } as ColumnDefinition,
        {
            header: Localizer.areasPanelActionsColumnLanguageItemName,
            minWidth: 125,
            removable: false,
            init: (cell) => this.initPriceOperations(cell),
            actions: [
                {
                    name: "cancel",
                    title: Localizer.genericCancelLanguageItemName,
                    icon: "far ban",
                    type: ActionType.Delete,
                    callback: async (cell, action) => await this.processPriceOperationAsync(cell, action)
                } as ColumnActionDefinition,
                {
                    name: "delete",
                    title: Localizer.genericDeleteLanguageItemName,
                    icon: "far trash-alt",
                    type: ActionType.Delete,
                    right: true,
                    callback: async (cell, action) => await this.processPriceOperationAsync(cell, action)
                } as ColumnActionDefinition,
                {
                    name: "restore",
                    title: Localizer.genericRestoreLanguageItemName,
                    icon: "far undo-alt",
                    type: ActionType.Create,
                    right: true,
                    callback: async (cell, action) => await this.processPriceOperationAsync(cell, action)
                } as ColumnActionDefinition
            ]
        } as ColumnDefinition,
    ];
    
    private getRows(items: CruiseAreaPrice[]): PriceRow[] {
        items = items.where(item => item.groupSize == this.groupSize);
        items.sortBy(item => item.duration);
        const rows: PriceRow[] = [];
        for (let i: number = 0; i < items.length; i++) {
            const price: CruiseAreaPrice = items[i];
            let row: PriceRow | null = rows.find(item => item.min == price.min && item.max == price.max) || null;
            if (row == null) {
                row = new PriceRow();
                rows.push(row);
            }
            row.setPrice(price);
        }
        rows.sortBy(item => item.min);
        return rows;
    }

    private isValidRowPrice(model: PriceRow): boolean {
        return (model.justRide > 0) || (model._1 > 0) || (model._2 > 0) || (model._3 > 0) || (model._4 > 0) || (model._5 > 0);
    }
    
    private isValidAmount(model: PriceRow): boolean {
        let isValid = (model.min > 0) && (model.min <= model.max);
        if (isValid) {
            const rows: PriceRow[] = this.pricesGrid.rows.where(row => !row.deleted).map(row => row.model);
            isValid = isValid && (!rows.some(item => (
                (item.id != model.id) &&
                (
                    ((item.min <= model.min) && (model.min <= item.max)) ||
                    ((item.min <= model.max) && (model.max <= item.max))
                )
            )));
        }
        return isValid;
    }

    private isValidRow(model: PriceRow): boolean {
        let isValid = (model.min > 0) && (model.min <= model.max);
        isValid = isValid && (this.isValidRowPrice(model));
        isValid = isValid && (this.isValidAmount(model));
        return isValid;
    }
    
    private get dataRows(): PriceRow[] {
        return (this._areasGridRef.current)
            ? this._rows ?? (this._rows = this.getRows(this.area.prices || []))
            : [];
    }

    private isValid(): boolean {
        return ((this._areasGridRef.current != null) && (
            (this.pricesGrid.rows.length == 0) ||
            (this.pricesGrid.rows.where(row => !row.deleted).every(row => this.isValidRow(row.model)))
        ));
    }

    private initAmount(cell: CellModel<PriceRow>): void {
        const model: PriceRow = cell.row.model;
        const isValid: boolean = (cell.row.deleted) || (this.isValidAmount(model));
        cell.valid = isValid;
        cell.className = this.cssIf(cell.className, !isValid, styles.invalid);
    }

    private initPrice(cell: CellModel<PriceRow>): void {
        const model: PriceRow = cell.row.model;
        const isValid: boolean = (cell.row.deleted) || (this.isValidRowPrice(model));
        cell.valid = isValid;
        cell.className = this.cssIf(cell.className, !isValid, styles.invalid);
    }

    private initPriceOperations(cell: CellModel<PriceRow>): void {
        const model: PriceRow = cell.row.model;
        const modified: boolean = cell.row.modified;
        const deleted: boolean = cell.row.deleted;

        const cancelAction: CellAction<PriceRow> = cell.actions[0];
        const deleteAction: CellAction<PriceRow> = cell.actions[1];
        const restoreAction: CellAction<PriceRow> = cell.actions[2];

        cancelAction.visible = (modified) && (!deleted) && (!model.isNew);
        deleteAction.visible = (!deleted);
        restoreAction.visible = (deleted);
    }

    private async processPriceOperationAsync(cell: CellModel<PriceRow>, action: CellAction<PriceRow>): Promise<void> {
        if (action.action.name === "cancel") {
            await cell.row.cancelAsync();
        } else if (action.action.name === "delete") {
            if (cell.model.isNew) {
                await cell.grid.deleteAsync(cell.rowIndex);
            } else {
                await cell.row.setDeletedAsync(true);
                await cell.grid.reRenderAsync();
            }
        } else if (action.action.name === "restore") {
            await cell.row.setDeletedAsync(false);
            await cell.grid.reRenderAsync();
        }
    }
    
    private async savePricesAsync(): Promise<void> {
        if (this.isValid()) {
            
            const prices: CruiseAreaPrice[] = this
                .pricesGrid
                .rows
                .where(row => !row.deleted)
                .selectMany(row => row.model.toPrices());
            
            const request = new SaveCruiseAreaPricesRequest();
            request.areaId = this.areaId;
            request.prices = prices;

            await this.pricesGrid.postAsync("api/cruise/saveCruiseAreaPrices", request);

            this.area.prices = prices;
            this._rows = null;
            
            await this.reloadAsync();
        }
    }

    private async cancelAsync(): Promise<void> {
        this._rows = null;

        await this.reloadAsync();
    }

    private async addPriceAsync(): Promise<void> {
        const model = new PriceRow();
        model.isNew = true;
        model.groupSize = this.groupSize;
        
        this.dataRows.push(model);

        await this.reloadAsync();
    }
    
    private async reloadAsync(): Promise<void> {
        await this.pricesGrid.reloadAsync();
        await this.reRenderAsync();
    }

    private get area(): CruiseArea {
        return this.props.area;
    }

    private get areaId(): string {
        return this.area.id;
    }

    private get groupSize(): CruiseGroupSize | null {
        return this.props.groupSize;
    }

    public get pricesGrid(): GridModel<PriceRow> {
        return this._areasGridRef.current!.model;
    }

    public render(): React.ReactNode {
        return (
            <React.Fragment>

                <div className={styles.areaPricesPanel}>

                    <Inline className={styles.toolbar} justify={JustifyContent.Start}>

                        <ToolbarButton icon={{name: "save", size: IconSize.Large}}
                                       label={Localizer.genericSave}
                                       type={ButtonType.Info}
                                       onClick={() => this.savePricesAsync()}
                        />

                        <ToolbarButton icon={{name: "far ban", size: IconSize.Large}}
                                       label={Localizer.genericCancel}
                                       type={ButtonType.Light}
                                       onClick={() => this.cancelAsync()}
                        />

                        <ToolbarButton icon={{name: "plus", size: IconSize.Large}}
                                       label={Localizer.genericAdd}
                                       title={Localizer.areaPricesPanelAddPriceTitle}
                                       type={ButtonType.Primary}
                                       onClick={() => this.addPriceAsync()}
                        />

                    </Inline>

                    <Grid ref={this._areasGridRef}
                          className={this.css(styles.grid)}
                          hovering={GridHoveringType.Row}
                          odd={GridOddType.None}
                          minWidth="auto"
                          noDataText={Localizer.areaPricesPanelNoPrices}
                          columns={this._areasColumns}
                          fetchData={async () => this.dataRows}
                    />
                    
                </div>

            </React.Fragment>
        );
    }
}